import { BodyFatLossDateRange } from '@reg/shared/model/identity/body-fat-lost-date-range.model';
import { DecimalPipe } from '@angular/common';

/**
 * Model to hold body assessment settings
 */
export class BodyAssessmentSettings {
    public minBodyFatPercentage: number = null;
    public dailyBodyFatLossPercentage: number = null;
    public minTolerancePercentage: number = null;
    public minToleranceIneligibleDays: number = null;
    public resetTolerancePercentage: number = null;
    public bodyFatLossDateRanges: BodyFatLossDateRange[] = [];

    public isEmpty(): boolean {
        return !this.minBodyFatPercentage &&
            !this.dailyBodyFatLossPercentage &&
            !this.minTolerancePercentage &&
            !this.minToleranceIneligibleDays &&
            !this.resetTolerancePercentage &&
            (!this.bodyFatLossDateRanges || !this.bodyFatLossDateRanges.length);
    }

    public clone(): BodyAssessmentSettings {
        const clone: BodyAssessmentSettings = new BodyAssessmentSettings();
        clone.minBodyFatPercentage = this.minBodyFatPercentage;
        clone.dailyBodyFatLossPercentage = this.dailyBodyFatLossPercentage;
        clone.minTolerancePercentage = this.minTolerancePercentage;
        clone.minToleranceIneligibleDays = this.minToleranceIneligibleDays;
        clone.resetTolerancePercentage = this.resetTolerancePercentage;

        if (this.bodyFatLossDateRanges) {
            clone.bodyFatLossDateRanges = this.bodyFatLossDateRanges.map((x: BodyFatLossDateRange) => x.clone());
        }
        return clone;
    }

    /**
     * Serialize to a json api format
     * @returns {string}
     */
    public serialize(): any {
        const bodyFatLossDateRanges: string[] = [];

        if (this.bodyFatLossDateRanges) {
            this.bodyFatLossDateRanges.forEach((x: BodyFatLossDateRange) => {
                bodyFatLossDateRanges.push(x.serialize());
            });
        }

        const dailyBfLossPct: number = this.dailyBodyFatLossPercentage &&
            parseFloat(this.dailyBodyFatLossPercentage.toString()) / 100;

        return {
            minBodyFatPercentage: this.minBodyFatPercentage ?
                parseFloat(this.minBodyFatPercentage.toString()) / 100 : null,
            dailyBodyFatLossPercentage: dailyBfLossPct || null,
            minTolerancePercentage: this.minTolerancePercentage ?
                parseFloat(this.minTolerancePercentage.toString()) / 100 : null,
            minToleranceIneligibleDays: this.minToleranceIneligibleDays ?
                parseInt(this.minToleranceIneligibleDays.toString(), null) : null,
            resetTolerancePercentage: this.resetTolerancePercentage ?
                parseFloat(this.resetTolerancePercentage.toString()) / 100 : null,
            bodyFatLossDateRanges: bodyFatLossDateRanges,
        };
    }

    /**
     * Deserialize to json api format
     */
    public deserialize(json: any): BodyAssessmentSettings {
        if (!json) {
            return;
        }
        const decimalPipe: DecimalPipe = new DecimalPipe('en-US');

        if (json.minBodyFatPercentage) {
            this.minBodyFatPercentage = parseFloat(decimalPipe.transform(json.minBodyFatPercentage * 100, '1.2-2'));
        }
        if (json.dailyBodyFatLossPercentage) {
            this.dailyBodyFatLossPercentage = parseFloat(
                decimalPipe.transform(json.dailyBodyFatLossPercentage * 100, '1.2-2'),
            );
        }
        if (json.minTolerancePercentage) {
            this.minTolerancePercentage = parseFloat(
                decimalPipe.transform(json.minTolerancePercentage * 100, '1.2-2'),
            );
        }
        if (json.resetTolerancePercentage) {
            this.resetTolerancePercentage = parseFloat(
                decimalPipe.transform(json.resetTolerancePercentage * 100, '1.2-2'),
            );
        }

        this.minToleranceIneligibleDays = json.minToleranceIneligibleDays;

        if (json.bodyFatLossDateRanges) {
            json.bodyFatLossDateRanges.forEach((range: any) => {
                this.bodyFatLossDateRanges.push(new BodyFatLossDateRange().deserialize(range));
            });
        }

        return this;
    }
}
